import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/lib/store";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { motion } from "framer-motion";
import { Clock, ArrowRight, Package, ChefHat, CheckCircle, XCircle, Star } from "lucide-react";

const STATUS_KEYS = {
  waiting_for_confirmation: "waiting_for_confirmation",
  accepted: "Accepted",
  rejected: "Rejected",
  paid: "Paid",
  preparing: "Preparing",
  ready: "Ready",
  completed: "Completed",
  cancelled: "Cancelled",
};

const STATUS_COLORS = {
  waiting_for_confirmation: "bg-[#F59E0B]/15 text-[#F59E0B]",
  accepted: "bg-[#3B82F6]/15 text-[#3B82F6]",
  rejected: "bg-[#EF4444]/15 text-[#EF4444]",
  paid: "bg-[#3B82F6]/15 text-[#3B82F6]",
  preparing: "bg-[#A855F7]/15 text-[#A855F7]",
  ready: "bg-[#22C55E]/15 text-[#22C55E]",
  completed: "bg-[#6B7280]/15 text-[#A8ACB4]",
  cancelled: "bg-[#EF4444]/15 text-[#EF4444]",
};

const STATUS_ICONS = {
  waiting_for_confirmation: Clock,
  accepted: CheckCircle,
  rejected: XCircle,
  paid: CheckCircle,
  preparing: ChefHat,
  ready: Package,
  completed: CheckCircle,
  cancelled: XCircle,
};

const ACTIVE_STATUSES = ["waiting_for_confirmation", "accepted", "paid", "preparing", "ready"];

function OrderCard({ order, index, t }) {
  const isActive = ACTIVE_STATUSES.includes(order.status);
  const StatusIcon = STATUS_ICONS[order.status] || Clock;
  const isDelivery = order.order_type === "delivery";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Link
        to={`/order/${order.id}`}
        className={`block bg-[#121212] rounded-xl border p-4 transition-colors ${
          isActive ? "border-[#D4B483]/30 hover:border-[#D4B483]/50" : "border-[#27272A] hover:border-[#3F3F46]"
        }`}
        data-testid={`order-item-${order.id}`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="font-mono-price text-sm font-bold text-[#EDEDED]">{order.order_number}</span>
            {isActive && (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D4B483] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D4B483]" />
              </span>
            )}
          </div>
          <Badge className={`${STATUS_COLORS[order.status] || "bg-[#27272A] text-[#D4D4D8]"} border-0 text-xs`}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {t(STATUS_KEYS[order.status] || order.status)}
          </Badge>
        </div>

        {/* Active order: show status hint */}
        {isActive && (
          <div className={`mb-2 text-xs font-medium ${
            order.status === "accepted" || order.status === "paid"
              ? "text-[#3B82F6]"
              : order.status === "preparing"
                ? "text-[#A855F7]"
                : order.status === "ready"
                  ? "text-[#22C55E]"
                  : "text-[#F59E0B]"
          }`}>
            {order.status === "accepted" && `✅ ${t("Confirmed — waiting for preparation")}`}
            {order.status === "paid" && `✅ ${t("Confirmed — waiting for preparation")}`}
            {order.status === "preparing" && `👨‍🍳 ${t("Your food is being prepared...")}`}
            {order.status === "ready" && `🎉 ${t("Ready for pickup!")}`}
            {order.status === "waiting_for_confirmation" && `⏳ ${t("Waiting for restaurant...")}`}
          </div>
        )}

        {/* Review stars for past orders */}
        {order.review && (
          <div className="flex items-center gap-0.5 mb-2">
            {[1, 2, 3, 4, 5].map(s => (
              <Star key={s} className={`w-3.5 h-3.5 ${s <= order.review.rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#27272A]"}`} />
            ))}
          </div>
        )}
        {!isActive && !order.review && (
          <div className="flex items-center gap-1 mb-2">
            <Star className="w-3 h-3 text-[#27272A]" />
            <span className="text-[10px] text-[#A8ABB2]">{t("No review yet")}</span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div>
            <p className="text-[#D4D4D8] text-xs">
              {order.items?.length || 0} {order.items?.length === 1 ? t("article") : t("articles")} · {isDelivery ? t("Delivery") : t("Pickup")}
            </p>
            <p className="text-[#D4D4D8] text-xs mt-0.5">
              <Clock className="w-3 h-3 inline mr-1" />
              {new Date(order.created_at).toLocaleDateString("sv-SE", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono-price text-[#D4B483] font-bold">{order.total.toFixed(0)} kr</span>
            <ArrowRight className="w-4 h-4 text-[#A8ABB2]" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function OrderHistoryPage() {
  const { token, t } = useApp();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { navigate("/auth", { state: { from: "/orders" } }); return; }
    const load = async () => {
      try {
        const res = await api.get("/orders");
        setOrders(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, navigate]);

  // Poll for live updates on active orders
  useEffect(() => {
    if (!token || loading || orders.length === 0) return;
    const hasActive = orders.some(o => ACTIVE_STATUSES.includes(o.status));
    if (!hasActive) return;

    const interval = setInterval(async () => {
      try {
        const res = await api.get("/orders");
        setOrders(res.data);
      } catch (e) { /* ignore */ }
    }, 8000);
    return () => clearInterval(interval);
  }, [token, loading, orders]);

  if (!token) return null;

  const activeOrders = orders.filter(o => ACTIVE_STATUSES.includes(o.status));
  const pastOrders = orders.filter(o => !ACTIVE_STATUSES.includes(o.status));

  return (
    <div className="p-4 md:p-8 max-w-lg mx-auto" data-testid="order-history-page">
      <span className="lux-eyebrow">{t("Bästa Biten")}</span>
      <h1 className="font-playfair text-3xl md:text-4xl text-[#F5F1E8] mt-3 mb-6 lux-title">{t("Your orders")}</h1>

      {loading ? (
        <div className="space-y-3">
          {Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-[#27272A] mx-auto mb-3" />
          <p className="text-[#D4D4D8]">{t("No orders yet")}</p>
          <Link to="/" className="text-[#D4B483] text-sm mt-2 inline-block hover:underline">{t("See menu")} →</Link>
        </div>
      ) : (
        <>
          {/* Active Orders Section */}
          {activeOrders.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D4B483] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#D4B483]" />
                </span>
                <h2 className="text-[#D4B483] text-sm font-semibold uppercase tracking-wider">
                  {t("Active orders")} ({activeOrders.length})
                </h2>
              </div>
              <div className="space-y-3">
                {activeOrders.map((order, i) => (
                  <OrderCard key={order.id} order={order} index={i} t={t} />
                ))}
              </div>
            </div>
          )}

          {/* Past Orders Section */}
          {pastOrders.length > 0 && (
            <div>
              {activeOrders.length > 0 && (
                <h2 className="text-[#A8ABB2] text-xs font-semibold uppercase tracking-wider mb-3">
                  {t("Previous orders")}
                </h2>
              )}
              <div className="space-y-3">
                {pastOrders.map((order, i) => (
                  <OrderCard key={order.id} order={order} index={i} t={t} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
